/**
 * As vantagens de cada cartão, pela BANDEIRA e pelo NÍVEL.
 *
 * Desenho: `docs/designs/vantagens-do-cartao.md` (08/10/2026).
 *
 * ── De onde vem cada linha ───────────────────────────────────────────────
 *
 * Da página oficial da bandeira, lida por gente e resumida com as nossas
 * palavras. Cada vantagem leva o link da página de onde saiu e a data em que
 * foi conferida. Sem fonte oficial, não entra: uma vantagem inventada faz
 * alguém viajar achando que tem seguro.
 *
 * Quando a página lida não dizia o passo a passo, a condição manda conferir
 * na fonte, em vez de inventar um.
 *
 * ── O que NÃO está aqui ──────────────────────────────────────────────────
 *
 * Pontos, milhas e cashback do banco; descontos e programas do próprio banco;
 * regra de anuidade. Só o que a bandeira publica para o nível do cartão.
 *
 * ── A condição é o que importa ───────────────────────────────────────────
 *
 * Quase todo seguro de cartão exige um passo que a pessoa não sabe: pagar a
 * passagem com o cartão, emitir um bilhete ANTES de viajar, recusar o seguro
 * da locadora. É aí que o benefício se perde, e por isso `condicao` é um
 * campo obrigatório, e não um detalhe do texto.
 *
 * ── Revisão ──────────────────────────────────────────────────────────────
 *
 * As bandeiras mudam as regras sem avisar. `revisadoEm` aparece na tela, e um
 * teste falha quando alguma linha passa de `MESES_ATE_REVISAR` sem conferência.
 */

export type TipoDeVantagem = 'seguro' | 'protecao' | 'acesso' | 'desconto' | 'servico';

export interface VantagemDoCartao {
  id: string;
  /** Como o Open Finance escreve: `MASTERCARD`, `VISA`. */
  bandeira: string;
  /** Os níveis em que vale (`BLACK`, `PLATINUM`). `*` = todo cartão da bandeira. */
  niveis: string[];
  tipo: TipoDeVantagem;
  nome: string;
  oQueE: string;
  /** O que a pessoa precisa fazer para o benefício valer. */
  condicao: string;
  /** A página oficial de onde isto saiu. */
  fonte: string;
  /** 'AAAA-MM-DD' da última conferência. */
  revisadoEm: string;
}

export const MESES_ATE_REVISAR = 6;

const MC = 'https://www.mastercard.com/br/pt/personal/find-a-card/credit-card/black-credit-card';
const MCP = 'https://www.mastercard.com/br/pt/personal/find-a-card/credit-card/platinum-credit-card';
const MCG = 'https://www.mastercard.com/br/pt/personal/find-a-card/credit-card/gold-credit-card';
const ELO = 'https://www.elo.com.br/cartoes';
const VISA = 'https://www.visa.com.br/pt_br/visa-ofertas-e-promocoes';
const HOJE = '2026-10-08';

export const VANTAGENS: VantagemDoCartao[] = [
  /* ── Mastercard Black ─────────────────────────────────────────────────── */
  {
    id: 'mc-black-seguro-medico', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'seguro',
    nome: 'Seguro médico em viagem ao exterior',
    oQueE: 'Despesas médicas até US$ 175 mil em viagens fora do Brasil, de até 60 dias. Vale também para quem viaja com passagem comprada no seu cartão.',
    condicao: 'Pague a passagem inteira com o cartão (ou as taxas, se emitiu com milhas) e emita o bilhete de seguro antes de embarcar.',
    fonte: `${MC}/masterassist-plus.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-cancelamento', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'seguro',
    nome: 'Cancelamento e atraso de viagem',
    oQueE: 'Até US$ 3 mil se a viagem for cancelada por motivo grave antes de começar, e até US$ 200 em hotel e alimentação por atraso de 4 horas ou mais no embarque.',
    condicao: 'Compre a passagem com o cartão e emita o bilhete de seguro antes da viagem.',
    fonte: `${MC}/inconveniencias-viagem.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-bagagem', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'seguro',
    nome: 'Bagagem atrasada ou perdida',
    oQueE: 'Até US$ 600 para comprar o necessário se a mala atrasar mais de 4 horas, e até US$ 3 mil se a companhia der a bagagem como perdida.',
    condicao: 'Compre a passagem com o cartão e emita o bilhete de seguro antes da viagem.',
    fonte: `${MC}/protecao-bagagem.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-carro', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'seguro',
    nome: 'Seguro do carro alugado',
    oQueE: 'Cobre colisão, roubo e incêndio do veículo alugado, até US$ 75 mil, por até 60 dias, no Brasil e no exterior. Não cobre danos a terceiros.',
    condicao: 'Pague a locação e a caução com o cartão, em seu nome, e recuse o seguro de colisão oferecido pela locadora.',
    fonte: `${MC}/masterseguro-automoveis.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-compra-protegida', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'protecao',
    nome: 'Compra protegida',
    oQueE: 'Reembolso se o produto for roubado ou sofrer dano acidental nos 90 dias depois da compra, até US$ 5 mil por ocorrência.',
    condicao: 'Compre com o cartão em loja ou site brasileiro e guarde o recibo. Em roubo, é preciso o boletim de ocorrência.',
    fonte: `${MC}/seguro-compra-protegida.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-garantia', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'protecao',
    nome: 'Garantia estendida',
    oQueE: 'Dobra a garantia do fabricante, em até 1 ano a mais, com cobertura de até US$ 2.500 por caso.',
    condicao: 'Compre com o cartão em loja ou site brasileiro, guarde o recibo e emita o bilhete de seguro.',
    fonte: `${MC}/garantia-estendida-original.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-sala-vip-gru', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'acesso',
    nome: 'Sala VIP em Guarulhos',
    oQueE: 'Sala no Terminal 3 para quem embarca em voo internacional. Acompanhante paga à parte.',
    condicao: 'Desde setembro de 2025 a gratuidade depende de um gasto mínimo no trimestre anterior, definido pelo seu banco. Confirme com ele antes de contar com o acesso.',
    fonte: `${MC}/sala-vip-guarulhos.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-airport', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'acesso',
    nome: 'Salas VIP em outros aeroportos',
    oQueE: 'Acesso a salas de aeroportos pelo programa Mastercard Airport Experiences.',
    condicao: 'Veja as regras de acesso na página do programa. Elas podem variar conforme o banco.',
    fonte: `${MC}.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-booking', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'desconto',
    nome: 'Cashback em hospedagem',
    oQueE: '6% de volta em reservas de hospedagem no Booking.com.',
    condicao: 'Veja na página da parceria como reservar para o cashback valer.',
    fonte: `${MC}/booking.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-black-surpreenda', bandeira: 'MASTERCARD', niveis: ['GOLD', 'PLATINUM', 'BLACK'], tipo: 'desconto',
    nome: 'Mastercard Surpreenda',
    oQueE: 'Programa de pontos e ofertas em restaurantes e outros parceiros: cada compra com o cartão vale um ponto.',
    condicao: 'Cadastre o cartão no programa. Sem o cadastro, as compras não pontuam.',
    fonte: 'https://surpreenda.naotempreco.com.br/', revisadoEm: HOJE,
  },
  {
    id: 'mc-black-concierge', bandeira: 'MASTERCARD', niveis: ['BLACK'], tipo: 'servico',
    nome: 'Concierge 24 horas',
    oQueE: 'Atendimento por telefone para reservas, organização de viagem e informações.',
    condicao: 'É só ligar para a central do Mastercard Black.',
    fonte: `${MC}/concierge.html`, revisadoEm: HOJE,
  },

  /* ── Visa Platinum, Signature e Infinite ──────────────────────────────── */
  {
    id: 'visa-platinum-seguro-medico', bandeira: 'VISA', niveis: ['PLATINUM', 'SIGNATURE', 'INFINITE'], tipo: 'seguro',
    nome: 'Seguro de emergência médica no exterior',
    oQueE: 'Assistência médica, hospitalar e odontológica em viagens internacionais de até 31 dias (60 no Infinite). Vale também para cônjuge e filhos dependentes com passagem no cartão.',
    condicao: 'Pague a passagem com o cartão (ou as taxas, se emitiu com milhas) e emita o bilhete de seguro com pelo menos 48 horas de antecedência.',
    fonte: `${VISA}/seguro-de-emergencia-medica-internacional/141824`, revisadoEm: HOJE,
  },
  {
    id: 'visa-platinum-medico-online', bandeira: 'VISA', niveis: ['PLATINUM', 'SIGNATURE', 'INFINITE'], tipo: 'servico',
    nome: 'Visa Médico Online',
    oQueE: 'Orientação médica virtual durante a viagem, ligada ao seguro de emergência médica.',
    condicao: 'Depende de o seguro de emergência médica estar valendo para a viagem.',
    fonte: `${VISA}/visa-medico-online/141825`, revisadoEm: HOJE,
  },
  {
    id: 'visa-platinum-airport', bandeira: 'VISA', niveis: ['PLATINUM', 'SIGNATURE', 'INFINITE'], tipo: 'desconto',
    nome: 'Visa Airport Companion',
    oQueE: 'Aplicativo com descontos em restaurantes, lojas e serviços de aeroportos. O acesso gratuito a sala VIP por ele é só do Visa Infinite.',
    condicao: 'Baixe o aplicativo Visa Airport Companion e cadastre o cartão.',
    fonte: `${VISA}/visa-airport-companion/154218`, revisadoEm: HOJE,
  },
  {
    id: 'visa-platinum-hoteis', bandeira: 'VISA', niveis: ['PLATINUM', 'SIGNATURE', 'INFINITE'], tipo: 'desconto',
    nome: 'Visa Luxury Hotel Collection',
    oQueE: 'Benefícios em hotéis selecionados pela Visa.',
    condicao: 'Veja na página do programa como reservar para os benefícios valerem.',
    fonte: `${VISA}/visa-luxury-hotel-collection/126511`, revisadoEm: HOJE,
  },
  {
    id: 'visa-platinum-concierge', bandeira: 'VISA', niveis: ['PLATINUM', 'SIGNATURE', 'INFINITE'], tipo: 'servico',
    nome: 'Visa Digital Concierge',
    oQueE: 'Assistente para confirmar voos, reservar restaurantes e conseguir ingressos.',
    condicao: 'Veja na página da Visa como falar com o concierge.',
    fonte: `${VISA}/visa-concierge/141826`, revisadoEm: HOJE,
  },
  {
    id: 'visa-platinum-saque', bandeira: 'VISA', niveis: ['PLATINUM', 'SIGNATURE', 'INFINITE'], tipo: 'servico',
    nome: 'Saque e cartão de emergência',
    oQueE: 'Se o cartão for perdido ou roubado em viagem, a Visa pode liberar um saque em dinheiro e emitir um cartão provisório.',
    condicao: 'Veja na página da Visa como acionar em caso de perda ou roubo.',
    fonte: `${VISA}/servico-de-saque-emergencial/141812`, revisadoEm: HOJE,
  },

  /* ── Mastercard Platinum ──────────────────────────────────────────────── */
  {
    id: 'mc-platinum-seguro-medico', bandeira: 'MASTERCARD', niveis: ['PLATINUM'], tipo: 'seguro',
    nome: 'Seguro médico em viagem ao exterior',
    oQueE: 'Despesas médicas até US$ 25 mil por pessoa em viagens fora do Brasil, de até 31 dias. Vale para cônjuge e filhos dependentes incluídos no bilhete.',
    condicao: 'Compre a passagem com o cartão e emita o bilhete de seguro antes de embarcar.',
    fonte: `${MCP}/masterassist-plus.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-platinum-carro', bandeira: 'MASTERCARD', niveis: ['PLATINUM'], tipo: 'seguro',
    nome: 'Seguro do carro alugado',
    oQueE: 'Cobre colisão, roubo e incêndio do veículo alugado, até US$ 75 mil, por até 31 dias, no Brasil e no exterior. Não cobre danos a terceiros.',
    condicao: 'Pague a locação e a caução com o cartão, em seu nome, e recuse o seguro de colisão oferecido pela locadora.',
    fonte: `${MCP}/masterseguro-automoveis.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-platinum-booking', bandeira: 'MASTERCARD', niveis: ['PLATINUM'], tipo: 'desconto',
    nome: 'Cashback em hospedagem',
    oQueE: '4% de volta em reservas de hospedagem no Booking.com.',
    condicao: 'Veja na página da parceria como reservar para o cashback valer.',
    fonte: `${MCP}/booking.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-platinum-concierge', bandeira: 'MASTERCARD', niveis: ['PLATINUM'], tipo: 'servico',
    nome: 'Concierge 24 horas',
    oQueE: 'Atendimento por telefone para organizar viagens, achar eventos e indicar restaurantes e serviços.',
    condicao: 'É só ligar para a central do Mastercard Platinum.',
    fonte: `${MCP}/concierge.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-travel-rewards', bandeira: 'MASTERCARD', niveis: ['PLATINUM', 'BLACK'], tipo: 'desconto',
    nome: 'Mastercard Travel Rewards',
    oQueE: 'Cashback e descontos em compras feitas em viagem, em lojas parceiras no exterior.',
    condicao: 'Veja no "Saiba mais" da página da bandeira quais lojas participam e como o cashback é creditado.',
    fonte: `${MCP}.html`, revisadoEm: HOJE,
  },

  /* ── Mastercard Gold ──────────────────────────────────────────────────── */
  {
    id: 'mc-gold-protecao-de-preco', bandeira: 'MASTERCARD', niveis: ['GOLD'], tipo: 'protecao',
    nome: 'Proteção de preço',
    oQueE: 'Reembolso da diferença se você encontrar o mesmo produto por um preço menor depois da compra.',
    condicao: 'Compre com o cartão. Alguns seguros exigem emitir o bilhete: confira na página antes de contar com ele.',
    fonte: `${MCG}.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-gold-compra-protegida', bandeira: 'MASTERCARD', niveis: ['GOLD'], tipo: 'protecao',
    nome: 'Compra protegida',
    oQueE: 'Reembolso por roubo ou dano acidental de produtos comprados com o cartão.',
    condicao: 'Compre com o cartão e guarde o recibo. Os limites e o prazo estão na página da bandeira.',
    fonte: `${MCG}.html`, revisadoEm: HOJE,
  },
  {
    id: 'mc-gold-garantia', bandeira: 'MASTERCARD', niveis: ['GOLD'], tipo: 'protecao',
    nome: 'Garantia estendida',
    oQueE: 'Dobra a garantia do fabricante ou da loja nas compras elegíveis.',
    condicao: 'Compre com o cartão e confira na página se é preciso emitir o bilhete de seguro.',
    fonte: `${MCG}.html`, revisadoEm: HOJE,
  },

  /* ── Visa Signature e Infinite (além do que o Platinum tem) ───────────── */
  {
    id: 'visa-bagagem-atraso', bandeira: 'VISA', niveis: ['SIGNATURE', 'INFINITE'], tipo: 'seguro',
    nome: 'Bagagem atrasada',
    oQueE: 'Indenização para as despesas que o atraso da mala causar durante a viagem.',
    condicao: 'Emita o bilhete de seguro viagem antes de embarcar. Os limites estão nos termos do benefício.',
    fonte: `${VISA}/cobertura-de-atraso-de-bagagem-ampliada/141814`, revisadoEm: HOJE,
  },
  {
    id: 'visa-bagagem-perda', bandeira: 'VISA', niveis: ['SIGNATURE', 'INFINITE'], tipo: 'seguro',
    nome: 'Bagagem perdida ou roubada',
    oQueE: 'Indenização em caso de extravio, roubo, furto ou destruição de toda a bagagem durante a viagem.',
    condicao: 'Veja os termos e os limites na página do benefício antes de viajar.',
    fonte: `${VISA}/cobertura-de-perda-ou-roubo-de-bagagem/141821`, revisadoEm: HOJE,
  },
  {
    id: 'visa-carro', bandeira: 'VISA', niveis: ['SIGNATURE', 'INFINITE'], tipo: 'seguro',
    nome: 'Seguro do carro alugado',
    oQueE: 'Proteção contra roubo e danos do veículo alugado, durante a locação.',
    condicao: 'Reserve e pague a locação com o cartão. Os limites estão nos termos do benefício.',
    fonte: `${VISA}/seguro-para-veiculos-de-locadora/141828`, revisadoEm: HOJE,
  },
  {
    id: 'visa-atraso-de-embarque', bandeira: 'VISA', niveis: ['SIGNATURE', 'INFINITE'], tipo: 'seguro',
    nome: 'Atraso de embarque',
    oQueE: 'Reembolso de hospedagem e alimentação quando o embarque atrasa 4 horas ou mais, em viagens aéreas, rodoviárias e marítimas cobertas.',
    condicao: 'Veja na página do benefício como acionar e o que está coberto.',
    fonte: `${VISA}/seguro-atraso-de-embarque/149145`, revisadoEm: HOJE,
  },
  {
    id: 'visa-cancelamento', bandeira: 'VISA', niveis: ['SIGNATURE', 'INFINITE'], tipo: 'seguro',
    nome: 'Cancelamento de viagem',
    oQueE: 'Cobertura para reduzir o prejuízo quando a viagem é cancelada.',
    condicao: 'Emita o bilhete de seguro viagem antes de embarcar. As situações cobertas estão nos termos.',
    fonte: `${VISA}/cancelamento-de-viagem/141815`, revisadoEm: HOJE,
  },
  {
    id: 'visa-perda-de-conexao', bandeira: 'VISA', niveis: ['INFINITE'], tipo: 'seguro',
    nome: 'Perda de conexão aérea',
    oQueE: 'Cobertura para quando você perde um voo de conexão.',
    condicao: 'Veja os termos na página do benefício antes de viajar.',
    fonte: `${VISA}/cobertura-de-perda-de-conexao-aerea/141820`, revisadoEm: HOJE,
  },
  {
    id: 'visa-infinite-lounge', bandeira: 'VISA', niveis: ['INFINITE'], tipo: 'acesso',
    nome: 'Sala VIP em Guarulhos e embarque rápido',
    oQueE: 'Sala VIP no embarque internacional do Terminal 3 de Guarulhos, e fila exclusiva de embarque em Guarulhos e no Galeão.',
    condicao: 'Veja as regras de acesso na página do benefício. Elas podem variar conforme o banco.',
    fonte: `${VISA}/visa-infinite-lounge/154219`, revisadoEm: HOJE,
  },

  /* ── Visa Gold, Signature e Infinite: compras ─────────────────────────── */
  {
    id: 'visa-protecao-de-compra', bandeira: 'VISA', niveis: ['GOLD', 'SIGNATURE', 'INFINITE'], tipo: 'protecao',
    nome: 'Proteção de compra',
    oQueE: 'Proteção contra roubo, furto qualificado ou dano acidental por até 90 dias depois da compra, para os produtos da lista do benefício.',
    condicao: 'Compre com o cartão e guarde o recibo. A lista de produtos e os limites estão nos termos.',
    fonte: `${VISA}/protecao-de-compra/141777`, revisadoEm: HOJE,
  },
  {
    id: 'visa-garantia-estendida', bandeira: 'VISA', niveis: ['GOLD', 'SIGNATURE', 'INFINITE'], tipo: 'protecao',
    nome: 'Garantia estendida',
    oQueE: 'Até 12 meses a mais de garantia para defeitos cobertos pela garantia do fabricante, em produtos acima de US$ 50.',
    condicao: 'Pague o produto com o cartão e emita o bilhete de seguro de garantia estendida.',
    fonte: `${VISA}/garantia-estendida-original/141811`, revisadoEm: HOJE,
  },

  /*
   * ── Elo ────────────────────────────────────────────────────────────────
   * A Elo funciona diferente das outras: em vez de uma lista fixa por nível,
   * a pessoa ESCOLHE os benefícios (Elo Flex), e quem não escolheu fica sem.
   * É por isso que a linha aqui é um convite a escolher, e não um seguro
   * específico: o que cada pessoa tem depende do que ela ativou.
   */
  {
    id: 'elo-nanquim-flex', bandeira: 'ELO', niveis: ['NANQUIM'], tipo: 'servico',
    nome: 'Até 6 benefícios à sua escolha',
    oQueE: 'No Elo Nanquim você monta o cartão: escolhe até 6 benefícios, sem custo adicional. A página cita sala VIP e seguro viagem entre as vantagens do cartão.',
    condicao: 'Os benefícios precisam ser escolhidos. Veja no app ou no site da Elo quais estão ativos no seu cartão, e confirme com o seu banco.',
    fonte: `${ELO}/elo-nanquim/`, revisadoEm: HOJE,
  },
  {
    id: 'elo-grafite-flex', bandeira: 'ELO', niveis: ['GRAFITE'], tipo: 'servico',
    nome: 'Até 3 benefícios à sua escolha',
    oQueE: 'No Elo Grafite você escolhe até 3 benefícios para o cartão, sem custo.',
    condicao: 'Os benefícios precisam ser escolhidos. Veja no app ou no site da Elo quais estão ativos no seu cartão, e confirme com o seu banco.',
    fonte: `${ELO}/elo-grafite/`, revisadoEm: HOJE,
  },
  {
    id: 'elo-mais-flex', bandeira: 'ELO', niveis: ['MAIS'], tipo: 'servico',
    nome: 'Benefício flexível à sua escolha',
    oQueE: 'No Elo Mais você escolhe o benefício flexível que mais combina com você, sem custo.',
    condicao: 'O benefício precisa ser escolhido. Veja no app ou no site da Elo se o seu está ativo, e confirme com o seu banco.',
    fonte: `${ELO}/elo-mais/`, revisadoEm: HOJE,
  },
  {
    id: 'elo-decolar', bandeira: 'ELO', niveis: ['GRAFITE', 'NANQUIM'], tipo: 'desconto',
    nome: 'Viagem em 10x sem juros na Decolar',
    oQueE: 'Passagens, hotéis, resorts e pacotes na Decolar em até 10 vezes sem juros.',
    condicao: 'Use o link da oferta na página da Elo e pague com o cartão.',
    fonte: `${ELO}/elo-grafite/`, revisadoEm: HOJE,
  },
  {
    id: 'elo-shows', bandeira: 'ELO', niveis: ['*'], tipo: 'desconto',
    nome: 'Pré-venda e desconto em shows',
    oQueE: 'Ingressos antecipados e com desconto para shows, festivais e eventos parceiros da Elo.',
    condicao: 'Cadastre o cartão no site da Elo. Cada evento tem a sua regra, mostrada na página.',
    fonte: `${ELO}/`, revisadoEm: HOJE,
  },

  /* ── Todo cartão Visa ─────────────────────────────────────────────────── */
  {
    id: 'visa-vai-de-visa', bandeira: 'VISA', niveis: ['*'], tipo: 'desconto',
    nome: 'Vai de Visa',
    oQueE: 'Programa de descontos em gastronomia, eletrônicos e outros parceiros.',
    condicao: 'Veja as ofertas e como participar na página do programa.',
    fonte: 'https://www.visa.com.br/pague-com-visa/cartoes/beneficios.html', revisadoEm: HOJE,
  },
];

export const NOME_DO_TIPO: Record<TipoDeVantagem, string> = {
  seguro: 'Seguros de viagem',
  protecao: 'Proteção de compras',
  acesso: 'Acesso',
  desconto: 'Descontos e programas',
  servico: 'Serviços',
};

/** A ordem em que os tipos aparecem: primeiro o que vale mais dinheiro. */
export const ORDEM_DOS_TIPOS: TipoDeVantagem[] = ['seguro', 'protecao', 'acesso', 'desconto', 'servico'];

/**
 * As vantagens de UM cartão.
 *
 * Com bandeira e nível, as do nível mais as de toda a bandeira. Só com a
 * bandeira (o banco não informou o nível), só as de toda a bandeira: mostrar
 * as de um nível que não se conhece seria chutar.
 */
export const vantagensDe = (bandeira?: string | null, nivel?: string | null): VantagemDoCartao[] => {
  const b = (bandeira ?? '').trim().toUpperCase();
  const n = (nivel ?? '').trim().toUpperCase();
  if (!b) return [];
  return VANTAGENS.filter(v => v.bandeira === b && (v.niveis.includes('*') || (!!n && v.niveis.includes(n))));
};

/**
 * As bandeiras e os níveis que a pessoa pode escolher à mão, no método.
 *
 * Os códigos são os que o Open Finance usa, para as duas origens caírem na
 * mesma linha da tabela. A lista é maior que a de vantagens de propósito:
 * escolher "Elo Nanquim" hoje não mostra vantagem nenhuma, e passa a mostrar
 * no dia em que a tabela ganhar a Elo, sem ela ter de voltar aqui.
 */
export const BANDEIRAS_E_NIVEIS: { bandeira: string; niveis: string[] }[] = [
  { bandeira: 'MASTERCARD', niveis: ['STANDARD', 'GOLD', 'PLATINUM', 'BLACK'] },
  { bandeira: 'VISA', niveis: ['CLASSIC', 'GOLD', 'PLATINUM', 'SIGNATURE', 'INFINITE'] },
  { bandeira: 'ELO', niveis: ['BASIC', 'MAIS', 'GRAFITE', 'NANQUIM'] },
  { bandeira: 'AMERICAN_EXPRESS', niveis: ['GOLD', 'PLATINUM'] },
  { bandeira: 'HIPERCARD', niveis: [] },
];
